import React from 'react';
import { InstalledBadge } from '../installed-badge';
import type { PricingBadgeProps } from '../pricing-badge';
import { PricingBadge } from '../pricing-badge';
import { useEssentials } from '../../contexts/essentials-context';
import { SetupIncompleteBadge } from '../setup-incomplete-badge';
import { PremiumSiteRequiredBadge } from '../premium-site-required-badge';
import { Box, Text } from '@wix/design-system';
import { appInfoBadgeDataHooks } from './data-hooks';
import type { AppBoxSkin } from '../app-box/app-box.types';

export interface AppInfoBadgeProps {
  isAppInstalled: boolean;
  isAppBuiltIn: boolean;
  isAppSetupIncomplete: boolean;
  isAppWithBenefits: boolean;
  isAppCompatible?: boolean;
  pricing: PricingBadgeProps;
  options: {
    showPricing?: boolean;
    showPremiumRequired: boolean;
    size?: 'tiny' | 'small';
  };
  dataHook?: string;
  skin?: AppBoxSkin;
}

export function AppInfoBadge({
  isAppInstalled,
  isAppBuiltIn,
  isAppSetupIncomplete,
  isAppWithBenefits,
  isAppCompatible = true,
  pricing,
  options,
  dataHook = appInfoBadgeDataHooks.APP_INFO_BADGE,
  skin = 'default',
}: AppInfoBadgeProps) {
  const { t } = useEssentials();
  const { showPricing = true, showPremiumRequired, size = 'tiny' } = options;

  let children: React.JSX.Element | null;
  if (!isAppCompatible) {
    children = (
      <Text size={size} weight="thin" secondary>
        {t('app.page.incompatible.app.notification.ctaText')}
      </Text>
    );
  } else if (isAppWithBenefits && !isAppInstalled) {
    children = (
      <Text size={size} weight="thin" skin="success" secondary>
        {t('app.pricing.bundle-free')}
      </Text>
    );
  } else if (isAppWithBenefits && isAppInstalled) {
    children = (
      <InstalledBadge size={size}>
        {t('pricing.label.benefit.installed')}
      </InstalledBadge>
    );
  } else if (showPremiumRequired) {
    children = (
      <PremiumSiteRequiredBadge size={size} showIcon={skin === 'default'} />
    );
  } else if (isAppBuiltIn) {
    children = (
      <InstalledBadge size={size}>
        {t('solutionBox.solutionBottomActionBar.builtInSolution')}
      </InstalledBadge>
    );
  } else if (isAppSetupIncomplete) {
    children = (
      <Box>
        <SetupIncompleteBadge />
      </Box>
    );
  } else if (isAppInstalled) {
    children = (
      <InstalledBadge size={size}>
        {t('solutionBox.solutionBottomActionBar.installedSolution')}
      </InstalledBadge>
    );
  } else if (showPricing && pricing.label) {
    children = <PricingBadge size={size} {...pricing} />;
  } else {
    children = null;
  }

  return children ? <div data-hook={dataHook}>{children}</div> : null;
}
